import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Divider, Popover, Radio, Text } from '@ui-kitten/components';
import lodashFindIndex from 'lodash/findIndex';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsEqual from 'lodash/isEqual';

import constants from '../../Config/constants';
import ThemeStyle from '../../Theme/styles';

import Button from '../Button';
import FakeScrollIndicator from '../FakeScrollIndicator';
import ListItem from '../ListItem';
import { textCategory } from '../Reservation/constants';

import { DropdownPopoverDataItem, DropdownPopoverTypes } from './types';

const DropdownPopover = ({
  data,
  disabled,
  selectionType = 'radio',
  trigger,
  value,
  onClose,
  onOpen,
  onSelect,
}: DropdownPopoverTypes) => {
  const scrollRef = useRef<ScrollView>();
  const scrollYloc = useRef<{ [key: string]: number }>({});
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [isVisible, setVisible] = useState(false);

  // Effect for auto scrolling on the selected item and flashing of scroll indicator
  useEffect(() => {
    let timeout = null;
    if (isVisible) {
      timeout = setTimeout(() => {
        scrollRef.current.flashScrollIndicators(); // scroll indicator flash on ios app
        if (scrollYloc.current[value.value]) {
          scrollRef.current.scrollTo({
            animated: false,
            y: scrollYloc.current[value.value] - 150, // 150 is half of max height of scrollview
          });
        }
      }, 100);
    }
    return () => {
      if (constants.isWeb) {
        setUserHasInteracted(false);
      }
      clearTimeout(timeout);
    };
  }, [isVisible, value]);

  // Effect for ensuring the selected value is in data
  useEffect(() => {
    const valueIndex = lodashFindIndex(data, (d) => d?.value === value?.value);
    if (!lodashIsEmpty(data) && !lodashIsEmpty(value) && valueIndex === -1) {
      // if the current value is not present on the data
      onSelect(data[0]); // call onSelect and pass 0 index
    }
  }, [data, value, onSelect]);

  const Anchor = useCallback(
    () => (
      <Button
        disabled={disabled || lodashIsEmpty(data)}
        onPress={() => {
          setVisible(true);
          onOpen?.();
        }}
        plain
      >
        {trigger}
      </Button>
    ),
    [disabled, trigger, onOpen],
  );

  const _onTouchStart = useCallback(() => {
    if (constants.isWeb) {
      setUserHasInteracted(true);
    }
  }, []);

  return (
    <View style={ThemeStyle.flex1}>
      <Popover
        anchor={Anchor}
        animationType="fade"
        visible={isVisible}
        onBackdropPress={() => {
          setVisible(false);
          onClose?.();
        }}
        style={[
          ThemeStyle.spacingTopMedium,
          ThemeStyle.spacingBottomMedium,
          ThemeStyle.shadowOverAll,
          styles.popoverContainer,
        ]}
        fullWidth
      >
        <View style={styles.contentContainer}>
          {constants.isWeb && (
            <FakeScrollIndicator userHasInteracted={userHasInteracted} />
          )}
          <ScrollView
            ref={scrollRef}
            indicatorStyle="black"
            onTouchStart={_onTouchStart} // no need to flip to false, we only need it on initial touch, we reset it on effect when not visible
            showsVerticalScrollIndicator
            persistentScrollbar // scroll indicator persistently on android app
          >
            {data?.map?.((item: DropdownPopoverDataItem, index: number) => {
              const isRadio = selectionType === 'radio';
              const isSelected = lodashIsEqual(value, item);
              const label = typeof item === 'string' ? item : item?.label;
              return (
                <View
                  key={index}
                  onLayout={(event) => {
                    const layout = event.nativeEvent.layout;
                    scrollYloc.current[label] = layout.y;
                  }}
                >
                  <ListItem
                    accessoryLeft={
                      isRadio && (
                        <Radio
                          status="info"
                          style={ThemeStyle.spacingRightMedium}
                          checked={isSelected}
                        />
                      )
                    }
                    contentContainerStyle={[
                      ThemeStyle.pageHorizontalSpacing,
                      ThemeStyle.pageVerticalSpacingMedium,
                    ]}
                    title={
                      <Text
                        category={textCategory.BODY}
                        status={!isRadio && isSelected ? 'info' : 'basic'}
                      >
                        {label}
                      </Text>
                    }
                    onPress={() => {
                      onSelect?.(item);
                      setVisible(false);
                    }}
                    flex={false}
                  />
                  {index !== data.length - 1 && (
                    <Divider style={ThemeStyle.divider} />
                  )}
                </View>
              );
            })}
          </ScrollView>
        </View>
      </Popover>
    </View>
  );
};

const styles = StyleSheet.create({
  popoverContainer: {
    borderRadius: 10,
  },
  contentContainer: {
    maxHeight: 300,
  },
});

export default memo(DropdownPopover);
